import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with Safari/iOS specific configuration
const axiosInstance = axios.create({
    baseURL: API,
    timeout: 60000, // 60 second timeout for slow mobile connections
    withCredentials: false,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    }
});

// Add request interceptor for cache-busting (Safari/iOS fix)
axiosInstance.interceptors.request.use(
    config => {
        // Add timestamp to URL params to prevent aggressive Safari caching
        const timestamp = Date.now();
        config.params = {
            ...config.params,
            _t: timestamp
        };
        
        return config;
    },
    error => Promise.reject(error)
);

// Add response interceptor with retry logic for network errors
axiosInstance.interceptors.response.use(
    response => response,
    async error => {
        const config = error.config;
        
        // If it's a network error and we haven't retried yet, retry once
        if (!error.response && !config._retry) {
            config._retry = true;
            console.log('Network error, retrying request:', config.url);
            
            // Wait a short moment before retrying
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            return axiosInstance(config);
        }
        
        // Log detailed error for debugging
        if (error.response) {
            console.error('API Error Response:', {
                status: error.response.status,
                data: error.response.data,
                url: error.config?.url
            });
        } else if (error.request) {
            console.error('API Network Error:', {
                url: error.config?.url,
                message: error.message,
                code: error.code
            });
        }
        return Promise.reject(error);
    }
);

// Also configure axios defaults for any direct axios usage
axios.defaults.withCredentials = false;
axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.headers.common['Content-Type'] = 'application/json';

export const api = {
    // Upload
    uploadImage: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return axiosInstance.post(`/upload/image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000 // Longer timeout for uploads
        });
    },
    
    // Recipes - using axiosInstance for better iPad compatibility
    getRecipes: (params = {}) => axiosInstance.get(`/recipes`, { params }),
    getFeaturedRecipes: () => axiosInstance.get(`/recipes/featured`),
    getRecipe: (id) => axiosInstance.get(`/recipes/${id}`),
    createRecipe: (data) => axiosInstance.post(`/recipes`, data),
    updateRecipe: (id, data) => axiosInstance.put(`/recipes/${id}`, data),
    deleteRecipe: (id) => axiosInstance.delete(`/recipes/${id}`),
    getMyRecipes: () => axiosInstance.get(`/my-recipes`),
    getRecipesUsingThis: (id) => axiosInstance.get(`/recipes/${id}/used-in`),
    searchRecipesForLinking: (query) => axiosInstance.get(`/recipes/search-for-linking`, { params: { q: query } }),
    
    // Comments & Ratings
    getRecipeComments: (recipeId, skip = 0, limit = 20) => axiosInstance.get(`/recipes/${recipeId}/comments`, { params: { skip, limit } }),
    createComment: (recipeId, data) => axiosInstance.post(`/recipes/${recipeId}/comments`, data),
    updateComment: (recipeId, commentId, data) => axiosInstance.put(`/recipes/${recipeId}/comments/${commentId}`, data),
    deleteComment: (recipeId, commentId) => axiosInstance.delete(`/recipes/${recipeId}/comments/${commentId}`),
    getRecipeRating: (recipeId) => axiosInstance.get(`/recipes/${recipeId}/rating`),
    getMyComments: (skip = 0, limit = 20) => axiosInstance.get(`/my-comments`, { params: { skip, limit } }),
    
    // User Preferences
    getUserPreferences: () => axiosInstance.get(`/user/preferences`),
    updateUserPreferences: (prefs) => axiosInstance.put(`/user/preferences`, prefs),
    
    // Password Reset
    forgotPassword: (email) => axiosInstance.post(`/auth/forgot-password`, { email }),
    resetPassword: (token, newPassword) => axiosInstance.post(`/auth/reset-password`, { token, new_password: newPassword }),
    verifyResetToken: (token) => axiosInstance.get(`/auth/verify-reset-token`, { params: { token } }),
    changePassword: (currentPassword, newPassword) => axiosInstance.post(`/auth/change-password`, { current_password: currentPassword, new_password: newPassword }),
    
    // Tips
    getTips: (params = {}) => axiosInstance.get(`/tips`, { params }),
    getTip: (id) => axiosInstance.get(`/tips/${id}`),
    createTip: (data) => axiosInstance.post(`/tips`, data),
    
    // Deals
    getDeals: (params = {}) => axiosInstance.get(`/deals`, { params }),
    getFeaturedDeals: () => axiosInstance.get(`/deals/featured`),
    
    // Weekly Pack
    getWeeklyPack: () => axiosInstance.get(`/weekly-pack/current`),
    getWeeklyPackById: (id) => axiosInstance.get(`/weekly-pack/${id}`),
    getWeeklyPacksHistory: (skip = 0, limit = 12) => axiosInstance.get(`/weekly-packs/history`, { params: { skip, limit } }),
    downloadPackPdf: async (packId = 'current') => {
        const response = await axiosInstance.get(`/weekly-pack/${packId}/pdf`, {
            responseType: 'blob'
        });
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `pack_${packId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return response;
    },
    
    // Admin
    checkAdmin: () => axiosInstance.get(`/auth/is-admin`),
    getAdminStats: () => axiosInstance.get(`/admin/stats`),
    
    // Admin - Users
    getAdminUsers: () => axiosInstance.get(`/admin/users`),
    giveUserPremium: (userId, durationDays = null) => axiosInstance.put(`/admin/users/${userId}/premium`, { duration_days: durationDays }),
    revokeUserPremium: (userId) => axiosInstance.put(`/admin/users/${userId}/revoke-premium`),
    
    // Admin - Admin Management
    getAdminList: () => axiosInstance.get(`/admin/admins`),
    makeUserAdmin: (userId) => axiosInstance.put(`/admin/users/${userId}/make-admin`),
    removeUserAdmin: (userId) => axiosInstance.put(`/admin/users/${userId}/remove-admin`),
    
    // Admin - Backup
    createBackup: () => axiosInstance.post(`/admin/backup`),
    listBackups: () => axiosInstance.get(`/admin/backups`),
    restoreBackup: (filename) => axiosInstance.post(`/admin/restore/${filename}`),
    
    // Admin - Packs
    getAdminPacks: () => axiosInstance.get(`/admin/weekly-packs`),
    createAdminPack: (data) => axiosInstance.post(`/admin/weekly-packs`, data),
    updateAdminPack: (id, data) => axiosInstance.put(`/admin/weekly-packs/${id}`, data),
    deleteAdminPack: (id) => axiosInstance.delete(`/admin/weekly-packs/${id}`),
    togglePackPublish: (id) => axiosInstance.put(`/admin/weekly-packs/${id}/publish`),
    sendPackNotification: (id) => axiosInstance.post(`/admin/weekly-packs/${id}/notify`),
    generateGroceryList: (id, includeUnpublishedDeals = false) => 
        axiosInstance.post(`/admin/weekly-packs/${id}/generate-grocery-list?include_unpublished_deals=${includeUnpublishedDeals}`),
    refreshGroceryDeals: (id) => axiosInstance.post(`/admin/weekly-packs/${id}/refresh-deals`),
    generateRecommendations: (id) => axiosInstance.post(`/admin/weekly-packs/${id}/generate-recommendations`),
    getGroceryListWithDeals: (id) => axiosInstance.get(`/admin/weekly-packs/${id}/grocery-list`),
    updateGroceryList: (id, groceryList) => axiosInstance.put(`/admin/weekly-packs/${id}/grocery-list`, { grocery_list: groceryList }),
    updateGroceryItemDeal: (packId, item, dealId) => axiosInstance.put(`/admin/weekly-packs/${packId}/grocery-item`, { item, deal_id: dealId }),
    
    // Admin - Recipes
    getAdminRecipes: (status = null) => axiosInstance.get(`/admin/recipes`, { params: status ? { status } : {} }),
    createAdminRecipe: (data) => axiosInstance.post(`/admin/recipes`, data),
    updateAdminRecipe: (id, data) => axiosInstance.put(`/admin/recipes/${id}`, data),
    approveRecipe: (id) => axiosInstance.post(`/admin/recipes/${id}/approve`),
    rejectRecipe: (id) => axiosInstance.put(`/admin/recipes/${id}/reject`),
    deleteAdminRecipe: (id) => axiosInstance.delete(`/admin/recipes/${id}`),
    importRecipeFromUrl: (url) => axiosInstance.post(`/admin/recipes/import-from-url`, { url, auto_calculate_costs: true }),
    
    // User - Personal Packs (Premium)
    generatePersonalPack: (criteria) => axiosInstance.post(`/user/generate-personal-pack`, criteria),
    getPersonalPacks: (limit = 5) => axiosInstance.get(`/user/personal-packs`, { params: { limit } }),
    
    // Ingredients Autocomplete (Premium)
    getIngredientsAutocomplete: (query = "") => axiosInstance.get(`/ingredients/autocomplete`, { params: { q: query } }),
    
    // Standardized Ingredients (Admin)
    getStandardizedIngredients: (query = "") => axiosInstance.get(`/admin/ingredients/standardized`, { params: { q: query, limit: 20 } }),
    normalizeIngredient: (ingredient) => axiosInstance.post(`/admin/ingredients/normalize`, null, { params: { ingredient } }),
    
    // Admin - Tips
    getAdminTips: (status = null) => axiosInstance.get(`/admin/tips`, { params: status ? { status } : {} }),
    createAdminTip: (data) => axiosInstance.post(`/admin/tips`, data),
    updateAdminTip: (id, data) => axiosInstance.put(`/admin/tips/${id}`, data),
    approveTip: (id) => axiosInstance.put(`/admin/tips/${id}/approve`),
    rejectTip: (id) => axiosInstance.put(`/admin/tips/${id}/reject`),
    deleteAdminTip: (id) => axiosInstance.delete(`/admin/tips/${id}`),
    
    // Admin - Deals
    getAdminDeals: () => axiosInstance.get(`/admin/deals`),
    createAdminDeal: (data) => axiosInstance.post(`/admin/deals`, data),
    updateAdminDeal: (id, data) => axiosInstance.put(`/admin/deals/${id}`, data),
    deleteAdminDeal: (id) => axiosInstance.delete(`/admin/deals/${id}`),
    toggleDealPublish: (id) => axiosInstance.put(`/admin/deals/${id}/publish`),
    publishAllDeals: () => axiosInstance.put(`/admin/deals/publish-all`),
    unpublishAllDeals: () => axiosInstance.put(`/admin/deals/unpublish-all`),
    extractDealsFromImage: (imageBase64, storeHint = '') => axiosInstance.post(`/admin/deals/extract-from-image`, { image_base64: imageBase64, store_hint: storeHint }),
    extractDealsFromPdf: (file, storeHint = '', maxPages = 3) => {
        console.log('Starting PDF extraction...', file.name, file.size, 'maxPages:', maxPages);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('store_hint', storeHint);
        formData.append('max_pages_to_process', maxPages.toString());
        return axiosInstance.post(`/admin/deals/extract-from-pdf`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 180000 // 3 minutes timeout for PDF processing
        });
    },
    bulkCreateDeals: (deals) => axiosInstance.post(`/admin/deals/bulk-create`, { deals }),
    
    // Admin - Dossiers spéciaux
    getAdminDossiers: () => axiosInstance.get(`/admin/dossiers`),
    createAdminDossier: (data) => axiosInstance.post(`/admin/dossiers`, data),
    updateAdminDossier: (id, data) => axiosInstance.put(`/admin/dossiers/${id}`, data),
    deleteAdminDossier: (id) => axiosInstance.delete(`/admin/dossiers/${id}`),
    
    // Public - Dossiers
    getDossiers: () => axiosInstance.get(`/dossiers`),
    getDossier: (slug) => axiosInstance.get(`/dossiers/${slug}`),
    
    // Favorites
    getFavorites: () => axiosInstance.get(`/favorites`),
    addFavorite: (recipeId) => axiosInstance.post(`/favorites/${recipeId}`),
    removeFavorite: (recipeId) => axiosInstance.delete(`/favorites/${recipeId}`),
    
    // User Grocery List
    getUserGroceryList: (packId) => axiosInstance.get(`/user/grocery-list/${packId}`),
    updateUserGroceryList: (packId, data) => axiosInstance.put(`/user/grocery-list/${packId}`, data),
    resetUserGroceryList: (packId) => axiosInstance.post(`/user/grocery-list/${packId}/reset`),
    checkFavorite: (recipeId) => axiosInstance.get(`/favorites/check/${recipeId}`),
    
    // Ingredient Substitution (Premium)
    getSubstitutionSuggestions: (ingredientName, reason = null) => 
        axiosInstance.post(`/user/ingredient-substitution/suggest`, { ingredient_name: ingredientName, reason }),
    substituteIngredient: (packId, data) => axiosInstance.post(`/user/grocery-list/${packId}/substitute`, data),
    getUserSubstitutions: (packId) => axiosInstance.get(`/user/substitutions/${packId}`),
    
    // Subscriptions
    getPlans: () => axiosInstance.get(`/subscriptions/plans`),
    createCheckout: (planId, originUrl) => axiosInstance.post(`/subscriptions/checkout`, { plan_id: planId, origin_url: originUrl }),
    getCheckoutStatus: (sessionId) => axiosInstance.get(`/subscriptions/status/${sessionId}`),
    getTrialStatus: () => axiosInstance.get(`/subscriptions/trial-status`),
    
    // AI
    suggestRecipe: (data) => axiosInstance.post(`/ai/suggest-recipe`, data, { timeout: 60000 }),
    suggestTip: () => axiosInstance.post(`/ai/suggest-tip`, {}, { timeout: 60000 }),
    suggestMealPlan: (data) => axiosInstance.post(`/ai/meal-plan`, data, { timeout: 60000 }),
    improveRecipe: (recipeId) => axiosInstance.post(`/ai/improve-recipe`, { recipe_id: recipeId }, { timeout: 60000 }),
    
    // Admin AI Generation
    generateRecipes: (count = 5) => axiosInstance.post(`/admin/ai/generate-recipes`, { count }, { timeout: 120000 }),
    generateTips: (count = 5) => axiosInstance.post(`/admin/ai/generate-tips`, { count }, { timeout: 120000 }),
    
    // Ingredient Prices
    getIngredientPrice: (name) => axiosInstance.get(`/ingredients/price`, { params: { name } }),
    searchIngredients: (q) => axiosInstance.get(`/ingredients/search`, { params: { q } }),
    estimateIngredientPrice: (ingredient, quantity) => axiosInstance.post(`/ingredients/estimate-price`, { ingredient, quantity }),
    
    // Categories
    getCategories: () => axiosInstance.get(`/categories`),
    
    // Stats
    getStats: () => axiosInstance.get(`/stats`),
    
    // Contact
    sendContactMessage: (data) => axiosInstance.post(`/contact`, data),
    
    // Analytics - Track page visits
    trackVisit: (path, referrer = null) => axiosInstance.post(`/track-visit`, { path, referrer }).catch(() => {}),
    
    // Seed
    seedData: () => axiosInstance.post(`/seed`),
};

// Export axiosInstance for use in other files (like AuthContext)
export { axiosInstance };
export default api;
